import * as React from 'react';

import { EnrollmentChoiceReasonCode } from '__generated__/graphql-types';
import type { CareerRole_PathwayLevel as PathwayLevel } from '__generated__/graphql-types';
import { compose } from 'recompose';

import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import type { ButtonProps } from '@coursera/cds-core';
import { Button } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';
import type { EventData } from '@coursera/event-pulse/sdk';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import {
  useFreeTrialCTACopyExperimentData,
  useGenerateStartFreeTrialTestEpicImpression,
} from 'bundles/coursera-plus/components/elevateLTV/startFreeTrial/useGenerateStartFreeTrialTestEpicImpression';
import { shouldShowEnrollForFreeText } from 'bundles/enroll/components/xdp/enrollSectionUtils';
import withEnrollModal from 'bundles/enroll/components/xdp/withEnrollModal';
import withEnrollment from 'bundles/enroll/components/xdp/withEnrollment';
import type { PropsFromWithEnrollment } from 'bundles/enroll/components/xdp/withEnrollment';
import { useGetS12nCourseMemberships } from 'bundles/enroll/hooks/useGetS12nCourseMemberships';
import EnrollmentChoices from 'bundles/enroll/models/EnrollmentChoices';
import type { Course } from 'bundles/enterprise-legacy-learner-home/types/xdpTypes';

import _t from 'i18n!nls/consumer-home';

type PropsFromCaller = {
  buttonProps: Partial<ButtonProps>;
  s12nId: string;
  partnerIds: Array<string>;
  courses: Array<Course>;
  children?: React.ReactNode;
  trackingData: EventData<'click_button'>;
  proficiencyLevel?: PathwayLevel;
};

type PropsFromWithEnrollModal = {
  onClick: () => void;
};

type PropsToComponent = PropsFromCaller & PropsFromWithEnrollment & PropsFromWithEnrollModal;

export const EnrollButtonContent = ({
  buttonProps,
  enrollmentAvailableChoices,
  onClick,
  trackingData,
}: PropsToComponent) => {
  const { isUserBasedFreeTrialCopyEnabled, isSessionBasedFreeTrialCopyEnabled } = useFreeTrialCTACopyExperimentData({
    enrollmentAvailableChoices,
  });
  const isFreeTrialCopyEnabled = isUserBasedFreeTrialCopyEnabled || isSessionBasedFreeTrialCopyEnabled;
  const showFreeText = shouldShowEnrollForFreeText({ enrollmentAvailableChoices });

  useGenerateStartFreeTrialTestEpicImpression({});

  const getButtonLabel = () => {
    if (enrollmentAvailableChoices?.isEnrolled) {
      return _t('Go to course');
    }

    if (showFreeText) {
      return isFreeTrialCopyEnabled ? _t('Start free trial') : _t('Enroll for free');
    }

    return _t('Enroll');
  };
  const trackV3 = useTracker();
  return (
    <Button
      {...buttonProps}
      onClick={() => {
        const updatedTrackingData = {
          ...trackingData,
          button: {
            ...trackingData.button,
            name: enrollmentAvailableChoices?.isEnrolled ? 'go_to_course' : trackingData.button.name,
          },
        };

        trackV3('click_button', updatedTrackingData);
        onClick();
      }}
    >
      {getButtonLabel()}
    </Button>
  );
};

// @ts-expect-error TSMIGRATION
const EnrollButtonWithEnrollModal = withEnrollModal(EnrollButtonContent);

const EnrollButtonWithProps = (props: PropsFromCaller & PropsFromWithEnrollment) => {
  const { courses, enrollmentAvailableChoices, s12nId, proficiencyLevel } = props;
  const router = useRouter();
  const { mostRecentS12nCourseMembership } = useGetS12nCourseMemberships({
    courses,
    skip: !enrollmentAvailableChoices?.isEnrolled,
  });

  const course = courses.find(({ id }) => mostRecentS12nCourseMembership?.courseId === id) ?? courses[0];

  const propsForWithEnrollModalHoc = {
    router,
    s12nId,
    courseId: course.id,
    slug: course.slug,
    isEnrolled: enrollmentAvailableChoices?.isEnrolled ?? false,
    isSpecialization: true,
    shouldCheckProductIdParam: true,
    ...(!user.isAuthenticatedUser() && proficiencyLevel
      ? { additionalQueryParams: { level: proficiencyLevel.toLowerCase() } }
      : {}),
  };

  return (
    <EnrollButtonWithEnrollModal
      {...props}
      isEligibleForPlusMonthlyToAnnualUpgrade={false}
      {...propsForWithEnrollModalHoc}
    />
  );
};

const EnrollButtonWithEnrollment = compose<PropsFromCaller & PropsFromWithEnrollment, PropsFromCaller>(
  withEnrollment(false)
)(EnrollButtonWithProps);

/**
 * @description This component is the wrapper for the EnrollButtonWithEnrollment component
 */
const RecommendedPathwayEnrollButtonNonConfigurable = (props: PropsFromCaller) => {
  return (
    <ErrorBoundaryWithTagging
      componentName="RecommendedPathwayEnrollButtonNonConfigurable"
      errorComponent={
        <EnrollButtonWithProps
          {...props}
          // If the EnrollmentChoices API fails, render the CTA and assume the learner is enrolled
          // so that the CTA takes them to course home, which then either loads if they are actually enrolled,
          // or redirects to the XDP if they're not enrolled
          enrollmentAvailableChoices={
            new EnrollmentChoices({
              enrollmentChoiceReasonCode: EnrollmentChoiceReasonCode.Enrolled,
              enrollmentChoices: [],
            })
          }
        />
      }
    >
      <EnrollButtonWithEnrollment {...props} />
    </ErrorBoundaryWithTagging>
  );
};

export default RecommendedPathwayEnrollButtonNonConfigurable;
